import {
  FC,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card } from "../Card";
import { Stack } from "components/Stack";
import { useAction, useHands, usePlayer, usePlayers, useSkip } from "hooks";
import { animated, useSpring, config, easings } from "@react-spring/web";
import { PokerAction, Street } from "types";
import { useStore } from "utils/store";
import { GreenBox } from "utils/constants";
import { getBb } from "utils/number";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import { useOrientation } from "hooks/orientation";

const PlayerStack = styled(animated.div)`
  position: absolute;
  width: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  transform-origin: left bottom;
  bottom: 0;
  min-height: 370px;

  @media (orientation: portrait) {
    min-height: 460px;
  }
`;

const Container = styled.div<{ $scale: number; $showLine: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  grid-row: 1;

  & > ${PlayerStack} {
    height: ${(props) => 100 / props.$scale}%;
  }

  @media (orientation: landscape) {
    &:nth-child(n + 2)::before {
      content: "";
      position: absolute;
      background-color: #d9a15e;
      width: 1px;
      height: 100%;
      bottom: 0;
      left: 0;
      opacity: ${(props) => (props.$showLine ? 0.3 : 0)};
      transition: opacity 0.5s;
    }
  }
`;

const Cards = styled.div`
  height: 76px;
  display: grid;
  grid-template-rows: 1fr 52px;
  grid-template-columns: auto auto;
  grid-gap: 0 4px;
  z-index: 1;

  @media (orientation: portrait) {
    height: 107px;
    grid-template-rows: 1fr 65px;
    grid-gap: 0 1px;
  }
`;

const Equity = styled(animated.div)<{ $percent: number; $scale: number }>`
  position: relative;
  width: 100%;
  line-height: 1;
  letter-spacing: normal;
  text-align: center;
  grid-column: 1 / 3;
  grid-row: 1 / 2;
  z-index: 0;
  color: ${(props) =>
    props.$percent < 20 ? "#ff1800" : props.$percent < 80 ? "#0086FF" : "#0f0"};
  font-size: 1.5rem;
  font-weight: 600;

  @media (orientation: portrait) {
    line-height: 1.5;
    font-size: 2.375rem;
  }
`;

const Text = styled(GreenBox)`
  position: absolute;
  left: calc(100% + 10px);
  bottom: 50px;
  padding: 7px 10px;
  pointer-events: none;

  @media (orientation: portrait) {
    font-size: 1.7rem;
    padding: 11px 20px;
  }
`;

export type PlayerProps = HTMLAttributes<HTMLDivElement> & {
  id: string;
  onHeroFinish: () => void;
  onPlayerFinish: (id: string) => void;
  onBalanceUpdate: () => void;
  onTopPlayer: () => void;
  onEliminated: () => void;
  onBountyUpdate: () => void;
};

export const Player: FC<PlayerProps> = ({
  id,
  onHeroFinish,
  onPlayerFinish,
  onBalanceUpdate,
  onTopPlayer,
  onEliminated,
  onBountyUpdate,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    index,
    balance,
    equity,
    bounty,
    holeCards,
    topPlayer,
    time,
    isBust,
    isActive,
  } = usePlayer(id);
  const { playerIds } = usePlayers();
  const street = useStore((state) => state.street);
  const hideState = useStore((state) => state.hideState);
  const isAllIn = useAction(id, PokerAction.AllIn);
  const isFold = useAction(id, PokerAction.Fold);
  const isAnte = useAction(id, PokerAction.Ante);
  const countRef = useRef(0);
  const bountyRef = useRef(0);
  const skip = useSkip();
  const [bb, setBb] = useState(-1);
  const heroFinishRef = useRef(onHeroFinish);
  const timeRef = useRef(0);
  const { bigBlind } = useHands();
  const [folding] = useDebounceValue(isFold, !isFold ? 0 : 750 + 150 * index);
  const balUpdateRef = useRef(onBalanceUpdate);
  const play = useSoundStore((store) => store.play);
  const equityShownRef = useRef(false);
  const [busting] = useDebounceValue(!bb && isBust, index * 250);
  const { portrait } = useOrientation();
  const transY = `${portrait && !!index ? 5 : 0}px`;
  const initialY = `scale(0) translateY(${transY})`;
  const neighborId = playerIds[index === 1 ? 4 : index === 4 ? 0 : index - 1];
  const neighbor = usePlayer(neighborId);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const showBounty = useStore((state) => state.showBounty);

  const [spring0, api0] = useSpring(() => ({
    transform: initialY,
    opacity: 1,
    config: (key) => {
      return key === "transform" ? config.wobbly : {};
    },
  }));

  const [spring1, api1] = useSpring(() => ({
    transform: `scale(1) translateX(0px)`,
    config: {
      duration: 750,
    },
  }));

  const [textSpring, textApi] = useSpring(() => ({
    opacity: 0,
    config: {
      duration: 250,
    },
  }));

  useEffect(() => {
    const w = containerRef.current?.clientWidth || 0;
    const h = containerRef.current?.clientHeight || 0;
    setScale(Math.min(w / 90, h / (portrait ? 460 : 370)));
  }, [
    containerRef.current?.clientWidth,
    containerRef.current?.clientHeight,
    portrait,
  ]);

  useEffect(() => {
    if (playerIds.length > 0) {
      api1.start({
        transform: `scale(${scale}) translateX(${
          ((containerRef.current?.clientWidth || 0) / scale - 90) / 2
        }px)`,
      });
    }
  }, [api1, playerIds.length, scale, containerRef.current?.clientWidth]);

  useEffect(() => {
    if (skip) {
      textApi.set({ opacity: 0 });
      timeRef.current = -1;
    }
  }, [skip, textApi]);

  useEffect(() => {
    heroFinishRef.current = onHeroFinish;
  }, [onHeroFinish]);

  useEffect(() => {
    balUpdateRef.current = onBalanceUpdate;
  }, [onBalanceUpdate]);

  useEffect(() => {
    if (skip && time < 0) balUpdateRef.current();
  }, [skip, time]);

  const debBb = useDebounceCallback(
    (n: number) => {
      setBb(n);
    },
    street === Street.PreFlop ? 250 * playerIds.length + 750 : 250
  );

  const newBb = useMemo(() => {
    return getBb(balance, bigBlind, isAnte);
  }, [balance, bigBlind, isAnte]);

  useEffect(() => {
    if ((!!index && countRef.current < 2) || skip) return;

    if (bb !== newBb) {
      if (time === 1 && !index) {
        textApi.start({
          opacity: 1,
          onRest: () => setBb(newBb),
        });
      } else if (bountyRef.current === bounty || !bountyRef.current) {
        debBb(newBb);
      }
    } else if (!index && timeRef.current !== time) {
      if (time < 0) {
        balUpdateRef.current();
      } else if (equity < 0) {
        heroFinishRef.current();
      }
    }
    timeRef.current = time;
  }, [bb, bounty, debBb, equity, index, newBb, skip, textApi, time]);

  useEffect(() => {
    if (bb === getBb(balance, bigBlind) && !hideState && topPlayer) {
      onTopPlayer();
    }
  }, [balance, bb, bigBlind, hideState, onTopPlayer, topPlayer]);

  useEffect(() => {
    if (folding || busting) {
      api0.start({
        opacity: 0,
        delay: folding ? 500 : 0,
        config: {
          duration: 250,
          easing: easings.easeOutSine,
        },
      });
    } else {
      api0.set({ transform: initialY, opacity: 1 });
    }
  }, [api0, busting, folding, initialY]);

  useEffect(() => {
    if (!holeCards.length) {
      countRef.current = 0;
    }
  }, [holeCards.length]);

  const cardStyle = (n: number) => ({
    transform: `scale(${!portrait ? 0.46 : !index ? 0.73 : 0.58}) translateY(${
      portrait && !index ? -11 : 0
    }px)`,
    transformOrigin: `${!n ? "right" : "left"} top`,
    marginLeft: `${!n ? "auto" : "0"}`,
    marginRight: `${!n ? "0" : "auto"}`,
    gridColumn: `${!n ? "1 / 2" : "2 / 3"}`,
  });

  useEffect(() => {
    if (!!hideState) {
      api0.start({
        opacity: 0,
        delay: hideState === 3 ? 500 : 0,
        config: {
          duration: hideState === 3 ? 500 : 1000,
          easing: easings.easeOutSine,
        },
        onRest: () => {
          api0.set({ transform: initialY, opacity: 1 });
        },
      });
    } else if (equity >= 0) {
      api0.start({
        transform: `scale(${
          portrait && !index ? 1.26 : 1
        }) translateY(${transY})`,
        delay: 250 * index,
        onStart: () => {
          play(Sounds.CardReveal);
        },
      });
    }
  }, [api0, equity, hideState, index, initialY, play, portrait, transY]);

  useEffect(() => {
    if (!index) {
      if (equity < 0) {
        equityShownRef.current = false;
      } else {
        if (equityShownRef.current) play(Sounds.Result);
        else equityShownRef.current = true;
      }
    }
  }, [equity, index, play]);

  useEffect(() => {
    if (!index && showBounty && bountyRef.current === bounty) onBountyUpdate();
  }, [bounty, index, onBountyUpdate, showBounty]);

  const roundEquity = useMemo(() => {
    return Math.round(equity);
  }, [equity]);

  const handleCards = () => {
    if (++countRef.current === 2) {
      if (bb !== newBb) {
        setBb(newBb);
      } else {
        onPlayerFinish(id);
      }
    }
  };

  const handleFinish = () => {
    if (!time) {
      onHeroFinish();
    } else if (time === 1) {
      if (!index) {
        textApi.start({
          opacity: 0,
          onRest: onHeroFinish,
        });
      } else {
        onPlayerFinish(id);
      }
    } else if (!index) {
      if (busting) {
        onEliminated();
      } else if (bountyRef.current === bounty && !!hideState) {
        onBalanceUpdate();
      } else if (bountyRef.current !== bounty) {
        onBountyUpdate();
        if (timeRef.current === time) setBb(newBb);
      }
    }
    bountyRef.current = bounty;
  };

  return (
    <Container
      ref={containerRef}
      $scale={scale}
      $showLine={!!neighborId && neighbor.isActive && isActive}
    >
      <PlayerStack style={{ ...spring1, zIndex: busting ? 1 : 0 }}>
        <Cards>
          <Equity $percent={roundEquity} style={spring0} $scale={scale}>
            {roundEquity}%
          </Equity>
          {!!holeCards.length &&
            holeCards.map((card, i) => (
              <Card
                key={i}
                index={i}
                card={card}
                style={cardStyle(i)}
                hideState={folding || busting ? 4 : hideState}
                skip={skip}
                onShow={handleCards}
              />
            ))}
        </Cards>
        {bb >= 0 && (
          <Stack
            id={id}
            bounty={bounty}
            onFinish={handleFinish}
            isAllIn={isAllIn}
            hideState={folding ? 4 : !index ? 0 : hideState}
            skip={skip}
            topPlayer={topPlayer}
            isBust={busting}
            newBb={bb}
            isFold={folding}
            scale={scale}
          />
        )}
        {!index && <Text style={textSpring}>{t('Updating chip stack…')}</Text>}
      </PlayerStack>
    </Container>
  );
};
